import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuditLogEntry, AuditLogService } from '../services/audit-log.service';
import { ContactService } from '../services/contact.service';
import { TeamAccessService } from '../services/team-access.service';
import { PropertyService } from '../services/property.service';
import { CompanyService } from '../services/company.service';
import { getServiceTypeTranslationKey, normalizeServiceTypeValue } from '../utils/service-type.utils';
import { normalizeTechnicalServiceCategoryKey } from '../utils/technical-service-categories';
import { roleNameKey } from '../utils/role-capabilities.utils';

type AuditOption = { value: string; labelKey: string };
type AuditDiffEntry = {
  field: string;
  before: any;
  after: any;
  changeType: 'updated' | 'added' | 'removed' | 'array';
  added?: any[];
  removed?: any[];
};
type ContactPersonAuditRow = {
  label: string;
  value: string;
};
type ContactPersonAuditCard = {
  heading: string;
  rows: ContactPersonAuditRow[];
};
type DocumentFileSummary = {
  fileName?: string | null;
};
type DocumentSectionSummary = {
  title?: string | null;
  files?: DocumentFileSummary[];
};
type DocumentChangeItem = {
  sectionTitle: string;
  sectionAdded: boolean;
  sectionRemoved: boolean;
  addedFiles: string[];
  removedFiles: string[];
};
type PropertyServiceChangeGroup = {
  serviceType: string;
  addedServices: string[];
  removedServices: string[];
};
type BulkDetailItem = {
  label: string;
  value: string;
};

@Component({
  selector: 'app-audit-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzDrawerModule,
    NzPaginationModule,
    NzDatePickerModule,
    NzTagModule,
    NzSpinModule,
    NzDescriptionsModule,
  ],
  templateUrl: './audit-log.component.html',
  styleUrl: './audit-log.component.less',
})
export class AuditLogComponent implements OnInit {
  logs: AuditLogEntry[] = [];
  selectedLog: AuditLogEntry | null = null;
  isLoading = false;
  isDrawerVisible = false;
  isDetailLoading = false;
  pageIndex = 1;
  pageSize = 20;
  total = 0;
  search = '';
  actor = '';
  selectedLogKind: string | null = null;
  selectedAction: string | null = null;
  selectedResourceType: string | null = null;
  dateRange: Date[] | null = null;

  readonly logKindOptions: AuditOption[] = [
    { value: 'audit', labelKey: 'AUDIT_LOGS.KINDS.audit' },
    { value: 'activity', labelKey: 'AUDIT_LOGS.KINDS.activity' },
  ];

  readonly actionOptions: AuditOption[] = [
    { value: 'company.updated', labelKey: 'AUDIT_LOGS.ACTIONS.company.updated' },
    { value: 'company.logo_uploaded', labelKey: 'AUDIT_LOGS.ACTIONS.company.logo_uploaded' },
    { value: 'company.technical_service_header_order_updated', labelKey: 'AUDIT_LOGS.ACTIONS.company.technical_service_header_order_updated' },
    { value: 'company.qr_poster_texts_updated', labelKey: 'AUDIT_LOGS.ACTIONS.company.qr_poster_texts_updated' },
    { value: 'company.qr_poster_template_created', labelKey: 'AUDIT_LOGS.ACTIONS.company.qr_poster_template_created' },
    { value: 'company.qr_poster_template_updated', labelKey: 'AUDIT_LOGS.ACTIONS.company.qr_poster_template_updated' },
    { value: 'company.qr_poster_template_set_active', labelKey: 'AUDIT_LOGS.ACTIONS.company.qr_poster_template_set_active' },
    { value: 'company.qr_poster_template_deleted', labelKey: 'AUDIT_LOGS.ACTIONS.company.qr_poster_template_deleted' },
    { value: 'landing_profile.created', labelKey: 'AUDIT_LOGS.ACTIONS.landing_profile.created' },
    { value: 'landing_profile.updated', labelKey: 'AUDIT_LOGS.ACTIONS.landing_profile.updated' },
    { value: 'landing_profile.deactivated', labelKey: 'AUDIT_LOGS.ACTIONS.landing_profile.deactivated' },
    { value: 'property.created', labelKey: 'AUDIT_LOGS.ACTIONS.property.created' },
    { value: 'property.updated', labelKey: 'AUDIT_LOGS.ACTIONS.property.updated' },
    { value: 'property.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.property.deleted' },
    { value: 'property.imported', labelKey: 'AUDIT_LOGS.ACTIONS.property.imported' },
    { value: 'template_headers.saved', labelKey: 'AUDIT_LOGS.ACTIONS.template_headers.saved' },
    { value: 'document.section.saved', labelKey: 'AUDIT_LOGS.ACTIONS.document.section.saved' },
    { value: 'document.section.updated', labelKey: 'AUDIT_LOGS.ACTIONS.document.section.updated' },
    { value: 'document.file.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.document.file.deleted' },
    { value: 'document.section.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.document.section.deleted' },
    { value: 'document.section.bulk_applied', labelKey: 'AUDIT_LOGS.ACTIONS.document.section.bulk_applied' },
    { value: 'document_section.updated', labelKey: 'AUDIT_LOGS.ACTIONS.document_section.updated' },
    { value: 'document_section.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.document_section.deleted' },
    { value: 'document_section.bulk_applied', labelKey: 'AUDIT_LOGS.ACTIONS.document_section.bulk_applied' },
    { value: 'document_section.bulk_edited', labelKey: 'AUDIT_LOGS.ACTIONS.document_section.bulk_edited' },
    { value: 'document_section.bulk_deleted', labelKey: 'AUDIT_LOGS.ACTIONS.document_section.bulk_deleted' },
    { value: 'emergency_instruction.created', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.created' },
    { value: 'emergency_instruction.updated', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.updated' },
    { value: 'emergency_instruction.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.deleted' },
    { value: 'emergency_instruction.bulk_applied', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.bulk_applied' },
    { value: 'emergency_instruction.bulk_edited', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.bulk_edited' },
    { value: 'emergency_instruction.bulk_deleted', labelKey: 'AUDIT_LOGS.ACTIONS.emergency_instruction.bulk_deleted' },
    { value: 'service.created', labelKey: 'AUDIT_LOGS.ACTIONS.service.created' },
    { value: 'service.updated', labelKey: 'AUDIT_LOGS.ACTIONS.service.updated' },
    { value: 'service.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.service.deleted' },
    { value: 'employee.created', labelKey: 'AUDIT_LOGS.ACTIONS.employee.created' },
    { value: 'employee.updated', labelKey: 'AUDIT_LOGS.ACTIONS.employee.updated' },
    { value: 'employee.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.employee.deleted' },
    { value: 'caretaker.deleted', labelKey: 'AUDIT_LOGS.ACTIONS.caretaker.deleted' },
    { value: 'company_user.invited', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.invited' },
    { value: 'company_user.invite_revoked', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.invite_revoked' },
    { value: 'company_user.invite_accepted', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.invite_accepted' },
    { value: 'company_user.updated', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.updated' },
    { value: 'company_user.deactivated', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.deactivated' },
    { value: 'company_user.reactivated', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.reactivated' },
    { value: 'company_user.removed', labelKey: 'AUDIT_LOGS.ACTIONS.company_user.removed' },
    { value: 'billing.properties_limit_increased', labelKey: 'AUDIT_LOGS.ACTIONS.billing.properties_limit_increased' },
    { value: 'billing.seat_limit_increased', labelKey: 'AUDIT_LOGS.ACTIONS.billing.seat_limit_increased' },
    { value: 'billing.config_updated', labelKey: 'AUDIT_LOGS.ACTIONS.billing.config_updated' },
    { value: 'billing.upgrade_contract_created', labelKey: 'AUDIT_LOGS.ACTIONS.billing.upgrade_contract_created' },
    { value: 'billing.upgrade_contract_replaced', labelKey: 'AUDIT_LOGS.ACTIONS.billing.upgrade_contract_replaced' },
    { value: 'billing.contract_signed', labelKey: 'AUDIT_LOGS.ACTIONS.billing.contract_signed' },
    { value: 'billing.company_plan_changed', labelKey: 'AUDIT_LOGS.ACTIONS.billing.company_plan_changed' },
    { value: 'billing.special_offer_requested', labelKey: 'AUDIT_LOGS.ACTIONS.billing.special_offer_requested' },
    { value: 'sign_order_created_with_legal_acceptance', labelKey: 'AUDIT_LOGS.ACTIONS.sign_order.created_with_legal_acceptance' },
    { value: 'branded_domain.connect_requested', labelKey: 'AUDIT_LOGS.ACTIONS.branded_domain.connect_requested' },
    { value: 'branded_domain.retry_requested', labelKey: 'AUDIT_LOGS.ACTIONS.branded_domain.retry_requested' },
    { value: 'branded_domain.refreshed', labelKey: 'AUDIT_LOGS.ACTIONS.branded_domain.refreshed' },
    { value: 'branded_domain.disconnect_requested', labelKey: 'AUDIT_LOGS.ACTIONS.branded_domain.disconnect_requested' },
  ];

  private readonly signOrderActionLabelKeys: Record<string, string> = {
    sign_order_created_with_legal_acceptance: 'AUDIT_LOGS.ACTIONS.sign_order.created_with_legal_acceptance',
    sign_order_payment_confirmed: 'AUDIT_LOGS.ACTIONS.sign_order.payment_confirmed',
    sign_order_status_changed: 'AUDIT_LOGS.ACTIONS.sign_order.status_changed',
    sign_order_notes_updated: 'AUDIT_LOGS.ACTIONS.sign_order.notes_updated',
    sign_order_email_retried: 'AUDIT_LOGS.ACTIONS.sign_order.email_retried',
    sign_order_settings_updated: 'AUDIT_LOGS.ACTIONS.sign_order_settings.updated',
    sign_product_updated: 'AUDIT_LOGS.ACTIONS.sign_product.updated',
    sign_product_image_uploaded: 'AUDIT_LOGS.ACTIONS.sign_product.image_uploaded',
    sign_product_image_updated: 'AUDIT_LOGS.ACTIONS.sign_product.image_updated',
    sign_product_image_deleted: 'AUDIT_LOGS.ACTIONS.sign_product.image_deleted',
    sign_product_images_reordered: 'AUDIT_LOGS.ACTIONS.sign_product.images_reordered',
  };

  private readonly signOrderResourceTypeLabelKeys: Record<string, string> = {
    SignOrder: 'AUDIT_LOGS.RESOURCE_TYPES.sign_order',
    SignOrderSettings: 'AUDIT_LOGS.RESOURCE_TYPES.sign_order_settings',
    SignProduct: 'AUDIT_LOGS.RESOURCE_TYPES.sign_product',
  };

  readonly resourceTypeOptions: AuditOption[] = [
    { value: 'company', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.company' },
    { value: 'landing_profile', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.landing_profile' },
    { value: 'company_invite', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.company_invite' },
    { value: 'company_user', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.company_user' },
    { value: 'employee', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.employee' },
    { value: 'service', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.service' },
    { value: 'property', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.property' },
    { value: 'property_import', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.property_import' },
    { value: 'template_header', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.template_header' },
    { value: 'property_documents', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.property_documents' },
    { value: 'emergency_instruction', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.emergency_instruction' },
    { value: 'company_billing_limit', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.company_billing_limit' },
    { value: 'billing_config', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.billing_config' },
    { value: 'contract_snapshot', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.contract_snapshot' },
    { value: 'special_offer_request', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.special_offer_request' },
    { value: 'SignOrder', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.sign_order' },
    { value: 'landing_page_custom_domain', labelKey: 'AUDIT_LOGS.RESOURCE_TYPES.landing_page_custom_domain' },
  ];

  private readonly actionLabelKeys = {
    ...Object.fromEntries(this.actionOptions.map((option) => [option.value, option.labelKey])),
    ...this.signOrderActionLabelKeys,
  };
  private readonly resourceTypeLabelKeys = {
    ...Object.fromEntries(this.resourceTypeOptions.map((option) => [option.value, option.labelKey])),
    ...this.signOrderResourceTypeLabelKeys,
  };
  private readonly resourceTypeDisplayAliases: Record<string, string> = {
    document_section: 'AUDIT_LOGS.RESOURCE_TYPES.property_documents',
    document_file: 'AUDIT_LOGS.RESOURCE_TYPES.property_documents',
    property_document_sections: 'AUDIT_LOGS.RESOURCE_TYPES.property_documents',
  };
  private readonly permissionLabelKeys: Record<string, string> = {
    'company.dashboard.read': 'PERMISSIONS.COMPANY_DASHBOARD_READ',
    'company.company_profile.read': 'PERMISSIONS.COMPANY_PROFILE_READ',
    'company.properties.write': 'PERMISSIONS.COMPANY_PROPERTIES_WRITE',
    'company.properties.read': 'PERMISSIONS.COMPANY_PROPERTIES_READ',
    'company.properties.delete': 'PERMISSIONS.COMPANY_PROPERTIES_DELETE',
    'company.properties.import': 'PERMISSIONS.COMPANY_PROPERTIES_IMPORT',
    'company.templates.read': 'PERMISSIONS.COMPANY_TEMPLATES_READ',
    'company.contacts.write': 'PERMISSIONS.COMPANY_CONTACTS_WRITE',
    'company.contacts.read': 'PERMISSIONS.COMPANY_CONTACTS_READ',
    'company.contacts.delete': 'PERMISSIONS.COMPANY_CONTACTS_DELETE',
    'company.announcements.read': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_READ',
    'company.announcements.write': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_WRITE',
    'company.announcements.send': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_SEND',
    'company.announcements.delete': 'PERMISSIONS.COMPANY_ANNOUNCEMENTS_DELETE',
    'company.company_profile.write': 'PERMISSIONS.COMPANY_PROFILE_WRITE',
    'company.property_documents.read': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_READ',
    'company.property_documents.write': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_WRITE',
    'company.property_documents.delete': 'PERMISSIONS.COMPANY_PROPERTY_DOCUMENTS_DELETE',
    'company.emergency_instructions.write': 'PERMISSIONS.COMPANY_EMERGENCY_INSTRUCTIONS_WRITE',
    'company.emergency_instructions.delete': 'PERMISSIONS.COMPANY_EMERGENCY_INSTRUCTIONS_DELETE',
    'company.ratings.read': 'PERMISSIONS.COMPANY_RATINGS_READ',
    'company.hospitality_ai.read': 'PERMISSIONS.COMPANY_HOSPITALITY_AI_READ',
    'company.templates.write': 'PERMISSIONS.COMPANY_TEMPLATES_WRITE',
    'company.qr_poster_texts.read': 'PERMISSIONS.COMPANY_QR_POSTER_TEXTS_READ',
    'company.qr_poster_texts.write': 'PERMISSIONS.COMPANY_QR_POSTER_TEXTS_WRITE',
    'company.hospitality_ai.qa.write': 'PERMISSIONS.COMPANY_HOSPITALITY_AI_QA_WRITE',
    'company.integrations.read': 'PERMISSIONS.COMPANY_INTEGRATIONS_READ',
    'company.integrations.write': 'PERMISSIONS.COMPANY_INTEGRATIONS_WRITE',
    'company.integrations.credentials.write': 'PERMISSIONS.COMPANY_INTEGRATIONS_CREDENTIALS_WRITE',
    'company.integrations.run': 'PERMISSIONS.COMPANY_INTEGRATIONS_RUN',
    'company.integrations.delete': 'PERMISSIONS.COMPANY_INTEGRATIONS_DELETE',
    'company.users.read': 'PERMISSIONS.COMPANY_USERS_READ',
    'company.users.invite': 'PERMISSIONS.COMPANY_USERS_INVITE',
    'company.users.update': 'PERMISSIONS.COMPANY_USERS_UPDATE',
    'company.users.deactivate': 'PERMISSIONS.COMPANY_USERS_DEACTIVATE',
    'company.users.remove': 'PERMISSIONS.COMPANY_USERS_REMOVE',
  };
  private readonly hiddenFieldPatterns = [
    'normalizedAddress',
    's3Key',
    'sourceS3Key',
    'uploadedAt',
    'sourcePropertyId',
    'sourceSectionId',
    'sourceInstructionId',
    'targetPropertyIds',
    'targetIds',
    'bulkGroupId',
    'originInstructionId',
    'originPropertyId',
    'clonedFromInstructionId',
    'contactRef.sourceId',
    'changedFieldCount',
    'activityType',
    'emergencyInstructionCount',
    'hospitalityConfigured',
    'sectionCount',
    'fileCount',
    'stepCount',
    'maxPropertyManagerCols',
    'maxTechnicalAssetsManagerCols',
    'maxSevManagerCols',
  ];
  private readonly weekdayKeys = new Set([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]);
  private readonly weekdayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  private readonly fieldLabelKeys: Record<string, string> = {
    companyName: 'AUDIT_LOGS.FIELDS.companyName',
    profileName: 'AUDIT_LOGS.FIELDS.profileName',
    displayEmail: 'AUDIT_LOGS.FIELDS.displayEmail',
    phone: 'AUDIT_LOGS.FIELDS.phone',
    whatsappNumber: 'AUDIT_LOGS.FIELDS.whatsappNumber',
    emergencyNumber: 'AUDIT_LOGS.FIELDS.emergencyNumber',
    themeColor: 'AUDIT_LOGS.FIELDS.themeColor',
    address: 'AUDIT_LOGS.FIELDS.address',
    website: 'AUDIT_LOGS.FIELDS.website',
    caretakerText: 'AUDIT_LOGS.FIELDS.caretakerText',
    publicLandingPageMessage: 'AUDIT_LOGS.FIELDS.publicLandingPageMessage',
    technicalServiceText: 'AUDIT_LOGS.FIELDS.technicalServiceText',
    showPropertyManager: 'AUDIT_LOGS.FIELDS.showPropertyManager',
    showSevManager: 'AUDIT_LOGS.FIELDS.showSevManager',
    showAccountant: 'AUDIT_LOGS.FIELDS.showAccountant',
    showAvailability: 'AUDIT_LOGS.FIELDS.showAvailability',
    showEmergencyAvailability: 'AUDIT_LOGS.FIELDS.showEmergencyAvailability',
    showTechnicalAssetsManager: 'AUDIT_LOGS.FIELDS.showTechnicalAssetsManager',
    showEmergencyNumber112: 'AUDIT_LOGS.FIELDS.showEmergencyNumber112',
    showEmergencyNumber110: 'AUDIT_LOGS.FIELDS.showEmergencyNumber110',
    showPoisonInfoCenterHotline: 'AUDIT_LOGS.FIELDS.showPoisonInfoCenterHotline',
    poisonInfoCenterHotlineNumber: 'AUDIT_LOGS.FIELDS.poisonInfoCenterHotlineNumber',
    showVeterinaryEmergencyHotline: 'AUDIT_LOGS.FIELDS.showVeterinaryEmergencyHotline',
    veterinaryEmergencyHotlineNumber: 'AUDIT_LOGS.FIELDS.veterinaryEmergencyHotlineNumber',
    showTelephoneCounselingHotline: 'AUDIT_LOGS.FIELDS.showTelephoneCounselingHotline',
    showChildrenYouthHotline: 'AUDIT_LOGS.FIELDS.showChildrenYouthHotline',
    showWomenGirlsEmergencyHotline: 'AUDIT_LOGS.FIELDS.showWomenGirlsEmergencyHotline',
    quickLinks: 'AUDIT_LOGS.FIELDS.quickLinks',
    hasLogo: 'AUDIT_LOGS.FIELDS.hasLogo',
    technicalServiceTypeOrder: 'AUDIT_LOGS.FIELDS.technicalServiceTypeOrder',
    technicalServiceCategoryOrder: 'AUDIT_LOGS.FIELDS.technicalServiceCategoryOrder',
    technicalServiceHeaderOrder: 'AUDIT_LOGS.FIELDS.technicalServiceHeaderOrder',
    landingPageEmergencyServiceTypes: 'AUDIT_LOGS.FIELDS.landingPageEmergencyServiceTypes',
    landingPageEmergencyServicePlacementEnabled: 'AUDIT_LOGS.FIELDS.landingPageEmergencyServicePlacementEnabled',
    qrPosterTexts: 'AUDIT_LOGS.FIELDS.qrPosterTexts',
    qrPosterTemplateName: 'AUDIT_LOGS.FIELDS.qrPosterTemplateName',
    qrPosterTemplateId: 'AUDIT_LOGS.FIELDS.qrPosterTemplateId',
    previousActiveTemplate: 'AUDIT_LOGS.FIELDS.previousActiveTemplate',
    newActiveTemplate: 'AUDIT_LOGS.FIELDS.newActiveTemplate',
    clearedLandingProfileCount: 'AUDIT_LOGS.FIELDS.clearedLandingProfileCount',
    whiteLabel: 'AUDIT_LOGS.FIELDS.whiteLabel',
    blockCount: 'AUDIT_LOGS.FIELDS.blockCount',
    sourceTemplate: 'AUDIT_LOGS.FIELDS.sourceTemplate',
    hospitalityLanding: 'AUDIT_LOGS.FIELDS.hospitalityLanding',
    hospitalityEntriesSummary: 'AUDIT_LOGS.FIELDS.hospitalityEntriesSummary',
    availabilitySummary: 'AUDIT_LOGS.FIELDS.availabilitySummary',
    emergencyAvailabilitySummary: 'AUDIT_LOGS.FIELDS.emergencyAvailabilitySummary',
    multiLandingProfilesEnabled: 'AUDIT_LOGS.FIELDS.multiLandingProfilesEnabled',
    active: 'AUDIT_LOGS.FIELDS.active',
    isDefault: 'AUDIT_LOGS.FIELDS.isDefault',
    caretakerId: 'AUDIT_LOGS.FIELDS.caretakerId',
    caretakerContactPersons: 'AUDIT_LOGS.FIELDS.caretakerContactPersons',
    landingProfileId: 'AUDIT_LOGS.FIELDS.landingProfileId',
    propertyManagerIds: 'AUDIT_LOGS.FIELDS.propertyManagerIds',
    technicalAssetsManagerIds: 'AUDIT_LOGS.FIELDS.technicalAssetsManagerIds',
    sevManagerIds: 'AUDIT_LOGS.FIELDS.sevManagerIds',
    accountantIds: 'AUDIT_LOGS.FIELDS.accountantIds',
    technicalServiceIds: 'AUDIT_LOGS.FIELDS.technicalServiceIds',
    technicalServiceAssignments: 'AUDIT_LOGS.FIELDS.technicalServiceAssignments',
    sectionTitle: 'AUDIT_LOGS.FIELDS.sectionTitle',
    documentSection: 'AUDIT_LOGS.FIELDS.documentSection',
    fileCount: 'AUDIT_LOGS.FIELDS.fileCount',
    fileName: 'AUDIT_LOGS.FIELDS.fileName',
    fileType: 'AUDIT_LOGS.FIELDS.fileType',
    files: 'AUDIT_LOGS.FIELDS.files',
    uploadedAt: 'AUDIT_LOGS.FIELDS.uploadedAt',
    addedFiles: 'AUDIT_LOGS.FIELDS.addedFiles',
    removedFiles: 'AUDIT_LOGS.FIELDS.removedFiles',
    changedFiles: 'AUDIT_LOGS.FIELDS.changedFiles',
    sourceProperty: 'AUDIT_LOGS.FIELDS.sourceProperty',
    sourcePropertyLabel: 'AUDIT_LOGS.FIELDS.sourcePropertyLabel',
    sourceSection: 'AUDIT_LOGS.FIELDS.sourceSection',
    sourceSectionTitle: 'AUDIT_LOGS.FIELDS.sourceSectionTitle',
    sourceInstruction: 'AUDIT_LOGS.FIELDS.sourceInstruction',
    sourceInstructionTitle: 'AUDIT_LOGS.FIELDS.sourceInstructionTitle',
    targetProperties: 'AUDIT_LOGS.FIELDS.targetProperties',
    targetPropertyIds: 'AUDIT_LOGS.FIELDS.targetProperties',
    targetCount: 'AUDIT_LOGS.FIELDS.targetCount',
    updated: 'AUDIT_LOGS.FIELDS.updated',
    deleted: 'AUDIT_LOGS.FIELDS.deleted',
    notFound: 'AUDIT_LOGS.FIELDS.notFound',
    skipped: 'AUDIT_LOGS.FIELDS.skipped',
    errors: 'AUDIT_LOGS.FIELDS.errors',
    bulkGroupId: 'AUDIT_LOGS.FIELDS.bulkGroupId',
    originInstructionId: 'AUDIT_LOGS.FIELDS.originInstructionId',
    originPropertyId: 'AUDIT_LOGS.FIELDS.originPropertyId',
    clonedFromInstructionId: 'AUDIT_LOGS.FIELDS.clonedFromInstructionId',
    serviceName: 'AUDIT_LOGS.FIELDS.serviceName',
    serviceNote: 'AUDIT_LOGS.FIELDS.serviceNote',
    serviceType: 'AUDIT_LOGS.FIELDS.serviceType',
    serviceTypes: 'AUDIT_LOGS.FIELDS.serviceTypes',
    serviceCategory: 'AUDIT_LOGS.FIELDS.serviceCategory',
    email: 'AUDIT_LOGS.FIELDS.email',
    mobile: 'AUDIT_LOGS.FIELDS.mobile',
    name: 'AUDIT_LOGS.FIELDS.name',
    note: 'AUDIT_LOGS.FIELDS.note',
    contactPerson: 'AUDIT_LOGS.FIELDS.contactPerson',
    contactPersons: 'AUDIT_LOGS.FIELDS.contactPersons',
    role: 'AUDIT_LOGS.FIELDS.role',
    companyRole: 'AUDIT_LOGS.FIELDS.companyRole',
    permissions: 'AUDIT_LOGS.FIELDS.permissions',
    isDeactivated: 'AUDIT_LOGS.FIELDS.isDeactivated',
    deactivationType: 'AUDIT_LOGS.FIELDS.deactivationType',
    lastLogin: 'AUDIT_LOGS.FIELDS.lastLogin',
    title: 'AUDIT_LOGS.FIELDS.title',
    category: 'AUDIT_LOGS.FIELDS.category',
    who: 'AUDIT_LOGS.FIELDS.who',
    how: 'AUDIT_LOGS.FIELDS.how',
    showPhoneNumber: 'AUDIT_LOGS.FIELDS.showPhoneNumber',
    summary: 'AUDIT_LOGS.FIELDS.summary',
    urgency: 'AUDIT_LOGS.FIELDS.urgency',
    actionType: 'AUDIT_LOGS.FIELDS.actionType',
    actionLabel: 'AUDIT_LOGS.FIELDS.actionLabel',
    contactSource: 'AUDIT_LOGS.FIELDS.contactSource',
    selectedContact: 'AUDIT_LOGS.FIELDS.selectedContact',
    contactRef: 'AUDIT_LOGS.FIELDS.contactSource',
    sourceType: 'AUDIT_LOGS.FIELDS.contactType',
    field: 'AUDIT_LOGS.FIELDS.contactField',
    scenarioKey: 'AUDIT_LOGS.FIELDS.scenarioKey',
    sourceLanguage: 'AUDIT_LOGS.FIELDS.sourceLanguage',
    iconKey: 'AUDIT_LOGS.FIELDS.iconKey',
    steps: 'AUDIT_LOGS.FIELDS.steps',
    welcomeMessage: 'AUDIT_LOGS.FIELDS.welcomeMessage',
    guestNotes: 'AUDIT_LOGS.FIELDS.guestNotes',
    showHostContact: 'AUDIT_LOGS.FIELDS.showHostContact',
    showHouseRules: 'AUDIT_LOGS.FIELDS.showHouseRules',
    showFeedbackChannel: 'AUDIT_LOGS.FIELDS.showFeedbackChannel',
    addressDetails: 'AUDIT_LOGS.FIELDS.addressDetails',
    propertyManagerHeaders: 'AUDIT_LOGS.FIELDS.propertyManagerHeaders',
    technicalAssetsManagerHeaders: 'AUDIT_LOGS.FIELDS.technicalAssetsManagerHeaders',
    sevManagerHeaders: 'AUDIT_LOGS.FIELDS.sevManagerHeaders',
    accountantHeaders: 'AUDIT_LOGS.FIELDS.accountantHeaders',
    caretakerHeaders: 'AUDIT_LOGS.FIELDS.caretakerHeaders',
    technicalServiceHeaders: 'AUDIT_LOGS.FIELDS.technicalServiceHeaders',
    maxProperties: 'AUDIT_LOGS.FIELDS.maxProperties',
    seatLimit: 'AUDIT_LOGS.FIELDS.seatLimit',
    maxPropertyManagerCols: 'AUDIT_LOGS.FIELDS.maxPropertyManagerCols',
    maxTechnicalAssetsManagerCols: 'AUDIT_LOGS.FIELDS.maxTechnicalAssetsManagerCols',
    maxSevManagerCols: 'AUDIT_LOGS.FIELDS.maxSevManagerCols',
    maxAccountantCols: 'AUDIT_LOGS.FIELDS.maxAccountantCols',
    displayName: 'AUDIT_LOGS.FIELDS.displayName',
    hostname: 'AUDIT_LOGS.FIELDS.hostname',
    status: 'AUDIT_LOGS.FIELDS.status',
    expiresAt: 'AUDIT_LOGS.FIELDS.expiresAt',
    acceptedAt: 'AUDIT_LOGS.FIELDS.acceptedAt',
  };
  private readonly auditFieldAliases: Record<string, string> = {
    sectiontitle: 'sectionTitle',
    section_title: 'sectionTitle',
    meta_section_title: 'sectionTitle',
    section: 'documentSection',
    document_section: 'documentSection',
    documentsection: 'documentSection',
    filename: 'fileName',
    file_name: 'fileName',
    file_file_name: 'fileName',
    filetype: 'fileType',
    file_type: 'fileType',
    file_file_type: 'fileType',
    uploadedat: 'uploadedAt',
    uploaded_at: 'uploadedAt',
    source_property: 'sourceProperty',
    sourceproperty: 'sourceProperty',
    source_property_label: 'sourcePropertyLabel',
    sourcepropertylabel: 'sourcePropertyLabel',
    source_section: 'sourceSection',
    sourcesection: 'sourceSection',
    source_section_title: 'sourceSectionTitle',
    sourcesectiontitle: 'sourceSectionTitle',
    source_instruction: 'sourceInstruction',
    sourceinstruction: 'sourceInstruction',
    source_instruction_title: 'sourceInstructionTitle',
    sourceinstructiontitle: 'sourceInstructionTitle',
    target_properties: 'targetProperties',
    targetpropertyids: 'targetProperties',
    target_property_ids: 'targetProperties',
    target_count: 'targetCount',
    targetcount: 'targetCount',
    file_count: 'fileCount',
    filecount: 'fileCount',
    added_files: 'addedFiles',
    addedfiles: 'addedFiles',
    removed_files: 'removedFiles',
    removedfiles: 'removedFiles',
    changed_files: 'changedFiles',
    changedfiles: 'changedFiles',
    not_found: 'notFound',
    notfound: 'notFound',
    bulk_group_id: 'bulkGroupId',
    bulkgroupid: 'bulkGroupId',
    origin_instruction_id: 'originInstructionId',
    origininstructionid: 'originInstructionId',
    origin_property_id: 'originPropertyId',
    originpropertyid: 'originPropertyId',
    cloned_from_instruction_id: 'clonedFromInstructionId',
    clonedfrominstructionid: 'clonedFromInstructionId',
    contact_source: 'contactSource',
    contactsource: 'contactSource',
    selected_contact: 'selectedContact',
    selectedcontact: 'selectedContact',
    contact_ref: 'contactRef',
    contactref: 'contactRef',
    source_type: 'sourceType',
    sourcetype: 'sourceType',
  };
  private readonly personLookup = new Map<string, string>();
  private readonly serviceLookup = new Map<string, string>();
  private readonly userLookup = new Map<string, string>();
  private readonly propertyLookup = new Map<string, string>();
  private readonly landingProfileLookup = new Map<string, string>();

  constructor(
    private auditLogService: AuditLogService,
    private contactService: ContactService,
    private teamAccessService: TeamAccessService,
    private propertyService: PropertyService,
    private companyService: CompanyService,
    private translate: TranslateService,
    private message: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.loadLookups();
    this.loadLogs();
  }

  loadLogs(): void {
    this.isLoading = true;
    this.auditLogService.listAuditLogs(this.buildFilters()).subscribe({
      next: (response) => {
        this.logs = response.items;
        this.pageIndex = response.page;
        this.pageSize = response.pageSize;
        this.total = response.total;
        this.isLoading = false;
      },
      error: () => {
        this.message.error(this.translate.instant('AUDIT_LOGS.ERRORS.LOAD'));
        this.isLoading = false;
      },
    });
  }

  applyFilters(): void {
    this.pageIndex = 1;
    this.loadLogs();
  }

  resetFilters(): void {
    this.search = '';
    this.actor = '';
    this.selectedLogKind = null;
    this.selectedAction = null;
    this.selectedResourceType = null;
    this.dateRange = null;
    this.pageIndex = 1;
    this.loadLogs();
  }

  onPageIndexChange(page: number): void {
    this.pageIndex = page;
    this.loadLogs();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.loadLogs();
  }

  openDetails(log: AuditLogEntry): void {
    this.isDrawerVisible = true;
    this.isDetailLoading = true;
    this.loadLookups();
    this.auditLogService.getAuditLog(log._id).subscribe({
      next: (entry) => {
        this.selectedLog = entry;
        this.isDetailLoading = false;
      },
      error: () => {
        this.message.error(this.translate.instant('AUDIT_LOGS.ERRORS.DETAIL'));
        this.isDetailLoading = false;
      },
    });
  }

  closeDrawer(): void {
    this.isDrawerVisible = false;
    this.selectedLog = null;
  }

  formatTimestamp(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = (this.translate.currentLang || 'en').toLowerCase() === 'de' ? 'de-DE' : 'en-US';
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }

  getActionLabel(action: string): string {
    const key = this.actionLabelKeys[action];
    if (key) {
      return this.translate.instant(key);
    }
    return this.humanizeAuditAction(action);
  }

  resourceTypeLabelKey(resourceType: string): string {
    return this.resourceTypeDisplayAliases[resourceType] || this.resourceTypeLabelKeys[resourceType] || 'AUDIT_LOGS.UNKNOWN_RESOURCE';
  }

  getResourceTypeDisplayLabel(log: AuditLogEntry): string {
    if (
      log.action === 'company.technical_service_header_order_updated'
      || log.resourceType === 'template_header'
      || log.resourceType === 'emergency_instruction'
      || log.resourceType === 'company_billing_limit'
      || log.resourceType === 'billing_config'
      || log.resourceType === 'contract_snapshot'
      || log.resourceType === 'SignOrderSettings'
    ) {
      return '';
    }
    if (log.resourceType === 'SignOrder' && !log.resourceLabel && !log.target?.resourceLabel) {
      return '';
    }
    return this.translate.instant(this.resourceTypeLabelKey(log.resourceType));
  }

  getActorLabel(log: AuditLogEntry): string {
    return log.actor?.displayName || log.actor?.email || this.translate.instant('AUDIT_LOGS.UNKNOWN_ACTOR');
  }

  getResourceLabel(log: AuditLogEntry): string {
    if (log.action === 'company.technical_service_header_order_updated') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.landing_page_configuration');
    }
    if (log.resourceType === 'template_header') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.landing_page_configuration');
    }
    if (log.resourceType === 'property_import') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.property_import');
    }
    if (log.resourceType === 'company_billing_limit') {
      if (log.action === 'billing.seat_limit_increased') {
        return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.seat_limit');
      }
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.property_limit');
    }
    if (log.resourceType === 'contract_snapshot') {
      if (log.action === 'billing.contract_signed') {
        return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.signed_contract');
      }
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.upgrade_contract_draft');
    }
    if (log.resourceType === 'billing_config') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.billing_configuration');
    }
    if (log.resourceType === 'SignOrderSettings') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.sign_order_settings');
    }
    if (log.resourceType === 'special_offer_request') {
      return this.translate.instant('AUDIT_LOGS.RESOURCE_LABELS.special_offer_request');
    }
    if (log.resourceType === 'emergency_instruction') {
      const propertyLabel = log.target?.resourceLabel || (log.target?.resourceId ? this.resolvePropertyLabel(log.target.resourceId) : '');
      const instructionLabel = log.resourceLabel || log.meta?.sourceInstructionTitle || this.translate.instant('AUDIT_LOGS.RESOURCE_TYPES.emergency_instruction');
      return [propertyLabel, instructionLabel].filter(Boolean).join(' · ') || this.translate.instant('AUDIT_LOGS.NO_RESOURCE_LABEL');
    }
    if (log.resourceType === 'document_section') {
      return log.resourceLabel
        || log.meta?.sourceSectionTitle
        || log.target?.resourceLabel
        || this.shortenResourceId(log.resourceId)
        || this.translate.instant('AUDIT_LOGS.NO_RESOURCE_LABEL');
    }
    if (log.resourceType === 'property_document_sections' && log.target?.resourceLabel) {
      return log.target.resourceLabel;
    }
    if (log.resourceType === 'SignOrder' && !log.resourceLabel && !log.target?.resourceLabel) {
      return this.translate.instant('AUDIT_LOGS.UNKNOWN_RESOURCE');
    }
    return log.resourceLabel
      || log.target?.resourceLabel
      || this.shortenResourceId(log.resourceId)
      || this.translate.instant('AUDIT_LOGS.NO_RESOURCE_LABEL');
  }

  getChangedFields(log: AuditLogEntry | null): string[] {
    return this.getDiffEntries(log).map((entry) => entry.field);
  }

  getLogKind(log: AuditLogEntry | null): 'audit' | 'activity' {
    return log?.logKind === 'activity' ? 'activity' : 'audit';
  }

  fieldLabel(fieldPath: string): string {
    if (fieldPath.startsWith('qrPosterTexts.')) {
      return this.qrPosterFieldLabel(fieldPath);
    }
    if (fieldPath.startsWith('technicalServiceHeaderOrder.')) {
      return this.translate.instant('AUDIT_LOGS.FIELD_PATTERNS.technicalServiceHeaderOrder', {
        category: this.categoryLabel(fieldPath.split('.')[1] || ''),
      });
    }
    if (fieldPath.startsWith('technicalServiceTypeOrder.')) {
      return this.translate.instant('AUDIT_LOGS.FIELD_PATTERNS.technicalServiceTypeOrder', {
        category: this.categoryLabel(fieldPath.split('.')[1] || ''),
      });
    }
    if (fieldPath.startsWith('technicalServiceCategoryOrder')) {
      return this.translate.instant('AUDIT_LOGS.FIELD_PATTERNS.technicalServiceCategoryOrder');
    }
    const availabilityLabel = this.availabilityFieldLabel(fieldPath);
    if (availabilityLabel) {
      return availabilityLabel;
    }
    const translationKey = this.resolveAuditFieldTranslationKey(fieldPath);
    if (translationKey) {
      return this.translate.instant(translationKey);
    }
    return this.humanizeAuditFieldLabel(fieldPath);
  }

  getDiffEntries(log: AuditLogEntry | null): AuditDiffEntry[] {
    if (log?.action === 'document.section.deleted') {
      return [];
    }

    const explicitEntries = Array.isArray(log?.changes?.diff?.entries) ? (log!.changes.diff.entries as AuditDiffEntry[]) : [];
    const fallbackEntries = explicitEntries.length ? explicitEntries : this.buildFallbackEntries(log);
    const visible = fallbackEntries
      .filter((entry: AuditDiffEntry) => !this.isHiddenField(entry.field) && !this.isSuppressedDiffField(log, entry.field))
      .filter((entry, _, all) => !this.isSuppressedAvailabilitySummaryParent(entry, all));

    return visible.sort((a, b) => {
      const aPriority = this.getAvailabilityDaySortPriority(a.field);
      const bPriority = this.getAvailabilityDaySortPriority(b.field);

      if (aPriority !== null && bPriority !== null) {
        const aPrefix = a.field.split('.')[0] || '';
        const bPrefix = b.field.split('.')[0] || '';
        if (aPrefix === bPrefix) {
          return aPriority - bPriority;
        }
      }

      return 0;
    });
  }

  getActivitySummary(log: AuditLogEntry | null): string {
    const activityType = log?.changes?.summary?.activityType;
    if (activityType === 'no_data_change') {
      return this.translate.instant('AUDIT_LOGS.ACTIVITY.no_data_change');
    }
    return this.translate.instant('AUDIT_LOGS.ACTIVITY.generic');
  }

  getChangeTypeLabel(changeType: string): string {
    return this.translate.instant(`AUDIT_LOGS.CHANGE_TYPES.${changeType}`);
  }

  formatDiffValue(value: any, field?: string, log?: AuditLogEntry | null): string {
    if (field === 'qrPosterTemplateId') {
      return this.formatQrPosterTemplateReferenceValue(value, log);
    }

    if (value === null || typeof value === 'undefined' || value === '') {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    if (this.isAvailabilityDayField(field)) {
      return this.formatAvailabilityValue(value);
    }

    if (field === 'companyRole') {
      return this.translate.instant(roleNameKey(String(value)));
    }

    if (field === 'role') {
      return this.formatRoleValue(value);
    }

    if (field === 'category') {
      return this.formatEmergencyCategoryValue(value);
    }

    if (field === 'sourceLanguage') {
      return this.formatSourceLanguageValue(value);
    }

    if (field === 'urgency') {
      return this.formatEmergencyUrgencyValue(value);
    }

    if (field === 'scenarioKey') {
      return this.formatEmergencyScenarioValue(value);
    }

    if (field === 'iconKey') {
      return this.formatEmergencyIconValue(value);
    }

    if (field === 'actionType') {
      return this.formatEmergencyActionTypeValue(value);
    }

    if (field === 'contactSource' || field === 'sourceType') {
      return this.formatEmergencyContactSourceValue(value);
    }

    if (field === 'field') {
      return this.formatEmergencyContactFieldValue(value);
    }

    if (field === 'permissions') {
      return this.formatPermissionValue(value);
    }

    if (field === 'serviceCategory' || field === 'technicalServiceCategoryOrder' || field?.startsWith('technicalServiceCategoryOrder')) {
      return this.formatCategoryValue(value);
    }

    if (
      field === 'serviceType'
      || field === 'serviceTypes'
      || field === 'landingPageEmergencyServiceTypes'
      || field?.startsWith('technicalServiceTypeOrder.')
      || field?.startsWith('technicalServiceHeaderOrder.')
    ) {
      return this.formatServiceTypeValue(value);
    }

    if (field === 'sourceTemplate') {
      return this.formatQrPosterTemplateSourceValue(value);
    }

    if (field === 'technicalServiceIds') {
      return this.formatServiceReferenceValue(value);
    }

    if (field === 'landingProfileId') {
      return this.formatLandingProfileReferenceValue(value);
    }

    if (field === 'propertyManagerIds' || field === 'technicalAssetsManagerIds' || field === 'sevManagerIds' || field === 'accountantIds' || field === 'caretakerId') {
      return this.formatPersonReferenceValue(value);
    }

    if (field === 'technicalServiceAssignments') {
      return this.formatTechnicalServiceAssignments(value);
    }

    if (field === 'contactPersons' || field === 'caretakerContactPersons') {
      return this.formatContactPersonsValue(value);
    }

    if (field === 'steps') {
      return this.formatEmergencyStepsValue(value);
    }

    if (field === 'addressDetails') {
      return this.formatAddressDetailValue(value);
    }

    if (field === 'status' && this.isSignOrderAuditLog(log)) {
      return this.formatSignOrderStatusValue(value);
    }

    if (field === 'status') {
      if (log?.resourceType === 'landing_page_custom_domain') {
        return this.formatBrandedDomainStatusValue(value);
      }
      return this.formatInviteStatusValue(value);
    }

    if (
      field === 'propertyManagerHeaders' ||
      field === 'technicalAssetsManagerHeaders' ||
      field === 'sevManagerHeaders' ||
      field === 'accountantHeaders' ||
      field === 'caretakerHeaders' ||
      field === 'technicalServiceHeaders'
    ) {
      return this.formatTemplateHeaderValue(value, field);
    }

    if (field === 'availabilitySummary' || field === 'emergencyAvailabilitySummary') {
      return this.formatAvailabilitySummaryValue(value);
    }

    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'AUDIT_LOGS.VALUES.TRUE' : 'AUDIT_LOGS.VALUES.FALSE');
    }

    if (Array.isArray(value)) {
      if (this.isFileSummaryArray(value)) {
        return this.formatFileList(value);
      }
      return value.length
        ? value.map((entry) => this.formatInlineValue(entry, field)).join(', ')
        : this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    if (typeof value === 'object') {
      return this.formatInlineValue(value, field);
    }

    if (typeof value === 'string') {
      const parsed = this.tryParseJsonValue(value);
      if (typeof parsed !== 'undefined') {
        return this.formatDiffValue(parsed, field, log);
      }
    }

    return String(value);
  }

  getContactPersonAuditCards(value: unknown, field?: string): ContactPersonAuditCard[] | null {
    if (!['contactPersons', 'caretakerContactPersons'].includes(field || '') || !Array.isArray(value) || !value.length) {
      return null;
    }

    return value.map((entry: unknown): ContactPersonAuditCard => {
      const person = this.asAuditRecord(entry);
      const name = this.contactPersonAuditText(person?.['name']);

      return {
        heading: name,
        rows: [
          { label: this.translate.instant('AUDIT_LOGS.FIELDS.name'), value: name },
          { label: this.translate.instant('CONTACTS.CONTACT_PERSON_POSITION'), value: this.contactPersonAuditText(person?.['position']) },
          { label: this.translate.instant('AUDIT_LOGS.FIELDS.phone'), value: this.contactPersonAuditText(person?.['phone']) },
          { label: this.translate.instant('AUDIT_LOGS.FIELDS.mobile'), value: this.contactPersonAuditText(person?.['mobile']) },
          { label: this.translate.instant('AUDIT_LOGS.FIELDS.email'), value: this.contactPersonAuditText(person?.['email']) },
          { label: this.translate.instant('CONTACTS.PRIMARY_CONTACT_PERSON'), value: this.contactPersonPrimaryAuditText(person?.['isPrimary']) },
        ],
      };
    });
  }

  private formatInlineValue(value: any, field?: string): string {
    if (value === null || typeof value === 'undefined' || value === '') {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'AUDIT_LOGS.VALUES.TRUE' : 'AUDIT_LOGS.VALUES.FALSE');
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.formatInlineValue(entry, field)).join(', ');
    }

    if (typeof value === 'object') {
      if (this.looksLikeFileSummary(value)) {
        return this.formatDocumentFileSummary(value);
      }
      if (field === 'technicalServiceAssignments' || Object.prototype.hasOwnProperty.call(value, 'serviceId')) {
        const serviceName = this.serviceLookup.get(String(value.serviceId || '')) || String(value.serviceId || '');
        const serviceType = value.serviceType ? this.translateServiceType(String(value.serviceType)) : '';
        return [serviceType, serviceName].filter(Boolean).join(' · ');
      }
      const preferredOrder = ['label', 'name', 'title', 'fileName', 'role', 'serviceCategory', 'serviceTypes', 'serviceType', 'serviceId', 'id'];
      const parts = preferredOrder
        .filter((key) => Object.prototype.hasOwnProperty.call(value, key) && value[key] !== null && typeof value[key] !== 'undefined')
        .map((key) => {
          if (key === 'role') {
            return this.formatRoleValue(value[key]);
          }
          if (key === 'serviceCategory') {
            return this.formatCategoryValue(value[key]);
          }
          if (key === 'serviceTypes' || key === 'serviceType') {
            return this.formatServiceTypeValue(value[key]);
          }
          return String(value[key]);
        });

      if (parts.length) {
        return parts.join(' · ');
      }

      return this.translate.instant('AUDIT_LOGS.VALUES.DETAILS_CHANGED');
    }

    if (typeof value === 'string') {
      const parsed = this.tryParseJsonValue(value);
      if (typeof parsed !== 'undefined') {
        return this.formatDiffValue(parsed, field);
      }
    }

    return String(value);
  }

  getDetailSummaryParts(log: AuditLogEntry | null): string[] {
    return log ? this.getSummaryParts(log).filter((part) => !/changedFieldCount/i.test(part)) : [];
  }

  private loadLookups(): void {
    this.personLookup.clear();
    this.serviceLookup.clear();
    this.userLookup.clear();
    this.propertyLookup.clear();
    this.landingProfileLookup.clear();

    forkJoin({
      employees: this.contactService.getEmployees().pipe(catchError(() => of([]))),
      caretakers: this.contactService.getCaretakers().pipe(catchError(() => of([]))),
      services: this.contactService.getServices().pipe(catchError(() => of([]))),
      users: this.teamAccessService.listUsers().pipe(catchError(() => of({ users: [] }))),
      properties: this.propertyService.getProperties().pipe(catchError(() => of([]))),
      landingProfiles: this.companyService.listLandingProfiles(true).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ employees, caretakers, services, users, properties, landingProfiles }) => {
        [...(Array.isArray(employees) ? employees : []), ...(Array.isArray(caretakers) ? caretakers : [])].forEach((employee: any) => {
          const id = employee?._id || employee?.id;
          if (!id) {
            return;
          }
          const label = employee?.name || employee?.displayName || employee?.email || String(id);
          this.personLookup.set(String(id), label);
        });

        (Array.isArray(services) ? services : []).forEach((service: any) => {
          const id = service?._id || service?.id;
          if (!id) {
            return;
          }
          const label = service?.serviceName || service?.serviceNote || String(id);
          this.serviceLookup.set(String(id), label);
        });

        const userItems = Array.isArray((users as any)?.users) ? (users as any).users : (Array.isArray(users) ? users : []);
        userItems.forEach((user: any) => {
          const id = user?._id || user?.id;
          if (!id) {
            return;
          }
          const label = user?.displayName || user?.email || String(id);
          this.userLookup.set(String(id), label);
        });

        (Array.isArray(properties) ? properties : []).forEach((property: any) => {
          const id = property?._id || property?.id;
          if (!id) {
            return;
          }
          const label = property?.address || String(id);
          this.propertyLookup.set(String(id), label);
        });

        (Array.isArray(landingProfiles) ? landingProfiles : []).forEach((profile: any) => {
          const id = profile?._id || profile?.id;
          if (!id) {
            return;
          }
          const label = profile?.profileName || profile?.name || String(id);
          this.landingProfileLookup.set(String(id), label);
        });
      },
    });
  }

  private formatLandingProfileReferenceValue(value: any): string {
    if (value === null || typeof value === 'undefined' || value === '') {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    const normalizedValue = String(value).trim();
    if (!normalizedValue) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    return this.landingProfileLookup.get(normalizedValue) || normalizedValue;
  }

  private formatQrPosterTemplateReferenceValue(value: unknown, log?: AuditLogEntry | null): string {
    const templateId = typeof value === 'string' ? value.trim() : '';
    if (!templateId) {
      return this.translate.instant('AUDIT_LOGS.VALUES.QR_POSTER_TEMPLATE_INHERIT');
    }

    const references = this.asAuditRecord(log?.meta?.qrPosterTemplateReferences);
    const snapshots = [
      this.asAuditRecord(references?.['before']),
      this.asAuditRecord(references?.['after']),
    ];
    const eventTimeName = snapshots.find((snapshot) => String(snapshot?.['id'] || '').trim() === templateId)?.['name'];
    if (typeof eventTimeName === 'string' && eventTimeName.trim()) {
      return eventTimeName.trim();
    }

    const lookupNames = this.asAuditRecord(log?.referenceLabels)?.['qrPosterTemplates'];
    const currentName = this.asAuditRecord(lookupNames)?.[templateId];
    if (typeof currentName === 'string' && currentName.trim()) {
      return currentName.trim();
    }

    return this.translate.instant('AUDIT_LOGS.VALUES.QR_POSTER_TEMPLATE_UNAVAILABLE');
  }

  private isHiddenField(fieldPath: string): boolean {
    const normalizedField = this.normalizeAuditFieldKey(fieldPath);
    return this.hiddenFieldPatterns.some((pattern) => {
      const normalizedPattern = this.normalizeAuditFieldKey(pattern);
      return fieldPath === pattern
        || fieldPath.endsWith(`.${pattern}`)
        || normalizedField === normalizedPattern
        || normalizedField.endsWith(`_${normalizedPattern}`);
    });
  }

  private isSuppressedDiffField(log: AuditLogEntry | null, fieldPath: string): boolean {
    if (!log) {
      return false;
    }
    if (log.resourceType === 'landing_page_custom_domain' && !['hostname', 'status'].includes(fieldPath)) {
      return true;
    }
    if (
      log.resourceType === 'template_header'
      && (fieldPath === 'addressDetails'
        || fieldPath === 'caretakerHeaders'
        || fieldPath === 'maxPropertyManagerCols'
        || fieldPath === 'maxTechnicalAssetsManagerCols'
        || fieldPath === 'maxSevManagerCols')
    ) {
      return true;
    }
    if (log.resourceType === 'emergency_instruction' && (fieldPath === 'version' || fieldPath === 'sourceLanguage')) {
      return true;
    }
    if (log.resourceType === 'emergency_instruction' && (fieldPath === 'who' || fieldPath === 'how')) {
      return true;
    }
    if (fieldPath === 'serviceType' && this.logContainsField(log, 'serviceTypes')) {
      return true;
    }
    if (log.action.startsWith('document.') && (fieldPath === 'sections' || fieldPath.endsWith('.sections'))) {
      return true;
    }
    if (log.resourceType === 'property' && (fieldPath === 'technicalServiceAssignments' || fieldPath === 'technicalServiceIds')) {
      return true;
    }
    if (log.action === 'company_user.invited' && (fieldPath === 'permissions' || fieldPath === 'expiresAt')) {
      return true;
    }
    if (log.action === 'company_user.invite_accepted' && (fieldPath === 'role' || fieldPath === 'permissions' || fieldPath === 'isDeactivated' || fieldPath === 'deactivationType')) {
      return true;
    }
    return false;
  }

  private isSuppressedAvailabilitySummaryParent(entry: AuditDiffEntry, allEntries: AuditDiffEntry[]): boolean {
    const field = entry.field;
    if (field !== 'availabilitySummary' && field !== 'emergencyAvailabilitySummary') {
      return false;
    }
    if (!this.isEmptyForAudit(entry.before) || !this.isEmptyForAudit(entry.after)) {
      return false;
    }
    const prefix = `${field}.`;
    return allEntries.some((candidate) => candidate.field?.startsWith(prefix));
  }

  private isEmptyForAudit(value: any): boolean {
    if (value === null || typeof value === 'undefined') {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    return false;
  }

  private buildFallbackEntries(log: AuditLogEntry | null): AuditDiffEntry[] {
    const hasBefore = Boolean(log?.changes?.before);
    const hasAfter = Boolean(log?.changes?.after);

    if (!log || (hasBefore && hasAfter) || (!hasBefore && !hasAfter)) {
      return [];
    }

    const entries: AuditDiffEntry[] = [];
    const source = hasAfter ? log.changes?.after : log.changes?.before;
    const changeType: AuditDiffEntry['changeType'] = hasAfter ? 'added' : 'removed';

    this.flattenForCreateEntries(source).forEach(([field, value]) => {
      if (changeType === 'added' && this.isEmptyForAudit(value)) {
        return;
      }
      entries.push({
        field,
        before: hasAfter ? null : value,
        after: hasAfter ? value : null,
        changeType,
      });
    });
    return entries;
  }

  private flattenForCreateEntries(value: any, prefix = ''): Array<[string, any]> {
    if (value === null || typeof value === 'undefined') {
      return [];
    }

    if (prefix && this.isAvailabilityDayField(prefix)) {
      return [[prefix, value]];
    }

    if (Array.isArray(value)) {
      if (!prefix) {
        return [];
      }
      return [[prefix, value]];
    }

    if (typeof value !== 'object') {
      return prefix ? [[prefix, value]] : [];
    }

    const entries = Object.entries(value);
    if (!entries.length) {
      return prefix ? [[prefix, value]] : [];
    }

    return entries.flatMap(([key, child]) => this.flattenForCreateEntries(child, prefix ? `${prefix}.${key}` : key));
  }

  private qrPosterFieldLabel(fieldPath: string): string {
    const [, language, key] = fieldPath.split('.');
    const languageLabel = (language || '').toLowerCase() === 'de'
      ? this.translate.instant('AUDIT_LOGS.LANGUAGES.de')
      : this.translate.instant('AUDIT_LOGS.LANGUAGES.en');
    const qrFieldMap: Record<string, string> = {
      title: 'QR_POSTER_TEMPLATE.FIELDS.TITLE.LABEL',
      subtitle: 'QR_POSTER_TEMPLATE.FIELDS.SUBTITLE.LABEL',
      intro: 'QR_POSTER_TEMPLATE.FIELDS.INTRO.LABEL',
      addressHeader: 'QR_POSTER_TEMPLATE.FIELDS.ADDRESS_HEADER.LABEL',
      companyHeader: 'QR_POSTER_TEMPLATE.FIELDS.COMPANY_HEADER.LABEL',
      cta: 'QR_POSTER_TEMPLATE.FIELDS.CTA.LABEL',
    };
    const fieldLabel = qrFieldMap[key] ? this.translate.instant(qrFieldMap[key]) : key;
    return this.translate.instant('AUDIT_LOGS.FIELD_PATTERNS.qrPosterText', { language: languageLabel, field: fieldLabel });
  }

  private categoryLabel(value: string): string {
    const categoryKey = normalizeTechnicalServiceCategoryKey(value) || value;
    const translationKeys = [
      `COMPANY_PAGE.TECHNICAL_SERVICE_CATEGORIES.${categoryKey}`,
      `COMPANY.TECHNICAL_SERVICE_CATEGORIES.${categoryKey}`,
    ];

    for (const translationKey of translationKeys) {
      const translated = this.translate.instant(translationKey);
      if (translated !== translationKey) {
        return translated;
      }
    }

    return value;
  }

  private availabilityFieldLabel(fieldPath: string): string | null {
    const availabilityKind = this.availabilityFieldKind(fieldPath);
    if (!availabilityKind) {
      return null;
    }

    const weekday = this.weekdaySegment(fieldPath);
    if (!weekday) {
      return null;
    }

    return this.translate.instant(
      availabilityKind === 'emergency'
        ? 'AUDIT_LOGS.FIELD_PATTERNS.emergencyAvailabilityDay'
        : 'AUDIT_LOGS.FIELD_PATTERNS.availabilityDay',
      { day: this.dayLabel(weekday) },
    );
  }

  private isAvailabilityDayField(fieldPath?: string): boolean {
    return Boolean(fieldPath && this.availabilityFieldKind(fieldPath) && this.weekdaySegment(fieldPath));
  }

  private isAvailabilityDayEntryField(fieldPath?: string): boolean {
    if (!fieldPath) { return false; }
    const kind = this.availabilityFieldKind(fieldPath);
    return kind !== null && !!this.weekdaySegment(fieldPath);
  }

  private getAvailabilityDaySortPriority(fieldPath?: string): number | null {
    if (!fieldPath || !this.isAvailabilityDayEntryField(fieldPath)) {
      return null;
    }
    const weekday = this.weekdaySegment(fieldPath)?.toLowerCase();
    if (!weekday) { return null; }
    const idx = this.weekdayOrder.indexOf(weekday);
    return idx >= 0 ? idx : null;
  }

  private availabilityFieldKind(fieldPath: string): 'standard' | 'emergency' | null {
    const root = fieldPath.split('.')[0]?.toLowerCase() || '';
    if (!root.includes('availability')) {
      return null;
    }
    return root.includes('emergency') ? 'emergency' : 'standard';
  }

  private weekdaySegment(fieldPath: string): string | null {
    return fieldPath.split('.').find((segment) => this.isWeekdayKey(segment)) || null;
  }

  private isWeekdayKey(value: string): boolean {
    return this.weekdayKeys.has(String(value || '').toLowerCase());
  }

  private dayLabel(value: string): string {
    const key = value?.toUpperCase?.() || value;
    const dayKey = `AUDIT_LOGS.WEEKDAYS.${key}`;
    const translated = this.translate.instant(dayKey);
    return translated === dayKey ? value : translated;
  }

  private translateServiceType(value: string): string {
    const directTranslation = this.translate.instant(`TECHNICAL_SERVICES.${value}`);
    if (directTranslation !== `TECHNICAL_SERVICES.${value}`) {
      return directTranslation;
    }
    const translationKey = getServiceTypeTranslationKey(normalizeServiceTypeValue(value));
    if (!translationKey) {
      return value;
    }
    return this.translate.instant(`TECHNICAL_SERVICES.${translationKey}`);
  }

  private formatServiceTypeValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values.map((entry) => this.translateServiceType(String(entry))).join(', ');
  }

  private formatContactPersonsValue(value: unknown): string {
    if (!Array.isArray(value)) {
      return this.formatLegacyContactPersonsValue(value);
    }

    const values = value;
    if (!values.length) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    return values
      .map((person: unknown) => {
        const record = this.asAuditRecord(person);
        const name = this.contactPersonAuditText(record?.['name']);
        const position = this.contactPersonAuditText(record?.['position']);
        const methods = [record?.['phone'], record?.['mobile'], record?.['email']]
          .map((method) => this.contactPersonAuditText(method))
          .filter((method) => method !== '—')
          .join(', ');
        const primary = record?.['isPrimary'] === true ? ` (${this.translate.instant('CONTACTS.PRIMARY_CONTACT_PERSON')})` : '';
        return [name, position === '—' ? '' : position, methods]
          .filter(Boolean)
          .join(' · ') + primary;
      })
      .join('; ');
  }

  private formatLegacyContactPersonsValue(value: unknown): string {
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) {
        return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
      }

      const parsed = this.tryParseJsonValue(normalized);
      if (Array.isArray(parsed)) {
        return this.formatContactPersonsValue(parsed);
      }
      if (typeof parsed === 'string') {
        return parsed.trim() || this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
      }
      if (typeof parsed === 'number') {
        return String(parsed);
      }
      if (typeof parsed === 'boolean') {
        return this.translate.instant(parsed ? 'AUDIT_LOGS.VALUES.TRUE' : 'AUDIT_LOGS.VALUES.FALSE');
      }
      if (typeof parsed !== 'undefined') {
        return this.translate.instant('AUDIT_LOGS.VALUES.DETAILS_CHANGED');
      }

      return normalized;
    }

    if (typeof value === 'number') {
      return String(value);
    }
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'AUDIT_LOGS.VALUES.TRUE' : 'AUDIT_LOGS.VALUES.FALSE');
    }

    return this.translate.instant('AUDIT_LOGS.VALUES.DETAILS_CHANGED');
  }

  private asAuditRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  }

  private contactPersonAuditText(value: unknown): string {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return '—';
    }

    const normalized = String(value).trim();
    return normalized || '—';
  }

  private contactPersonPrimaryAuditText(value: unknown): string {
    if (value === true || value === 'true') {
      return this.translate.instant('AUDIT_LOGS.VALUES.TRUE');
    }
    if (value === false || value === 'false') {
      return this.translate.instant('AUDIT_LOGS.VALUES.FALSE');
    }
    return '—';
  }

  private formatQrPosterTemplateSourceValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim();
        switch (normalized) {
          case 'scanafy-classic':
            return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_SCANAFY_CLASSIC_NAME');
          case 'emergency-notice':
            return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_EMERGENCY_NOTICE_NAME');
          case 'minimal-white-label':
            return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_MINIMAL_WHITE_LABEL_NAME');
          case 'system-template':
            return this.translate.instant('AUDIT_LOGS.VALUES.QR_POSTER_SOURCE_SYSTEM_TEMPLATE');
          case 'custom':
            return this.translate.instant('AUDIT_LOGS.VALUES.QR_POSTER_SOURCE_CUSTOM_TEMPLATE');
          default:
            return normalized || this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
        }
      })
      .join(', ');
  }

  private formatEmergencyCategoryValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    const categoryMap: Record<string, string> = {
      key_access: 'PROPERTY_INSTRUCTIONS.CATEGORY.KEY_ACCESS',
      main_shut_off: 'PROPERTY_INSTRUCTIONS.CATEGORY.MAIN_SHUT_OFF',
      emergency_tool_supply: 'PROPERTY_INSTRUCTIONS.CATEGORY.EMERGENCY_TOOL_SUPPLY',
      special_building_rule: 'PROPERTY_INSTRUCTIONS.CATEGORY.SPECIAL_BUILDING_RULE',
      other: 'PROPERTY_INSTRUCTIONS.CATEGORY.OTHER',
    };
    return values
      .map((entry) => {
        const normalized = String(entry);
        const key = categoryMap[normalized];
        if (!key) {
          return normalized;
        }
        const translated = this.translate.instant(key);
        return translated === key ? normalized : translated;
      })
      .join(', ');
  }

  private formatSourceLanguageValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toLowerCase();
        const translationKey = normalized === 'de' ? 'AUDIT_LOGS.LANGUAGES.de' : normalized === 'en' ? 'AUDIT_LOGS.LANGUAGES.en' : null;
        if (!translationKey) {
          return String(entry);
        }
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatEmergencyUrgencyValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toUpperCase();
        const translationKey = `PROPERTY_INSTRUCTIONS.URGENCY.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatEmergencyScenarioValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toUpperCase();
        const translationKey = `PROPERTY_INSTRUCTIONS.SCENARIO.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatEmergencyIconValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toUpperCase();
        const translationKey = `PROPERTY_INSTRUCTIONS.ICON.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatEmergencyContactSourceValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    const keyMap: Record<string, string> = {
      caretaker: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_CARETAKER',
      property_manager: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_PROPERTY_MANAGER',
      technical_assets_manager: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_TECHNICAL_ASSETS_MANAGER',
      sev_manager: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_SEV_MANAGER',
      accountant: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_ACCOUNTANT',
      technical_service: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_TECHNICAL_SERVICE',
      manual: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_MANUAL',
      assigned_property_contact: 'PROPERTY_INSTRUCTIONS.CONTACT_SOURCE_ASSIGNED_CONTACT',
    };

    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toLowerCase();
        const translationKey = keyMap[normalized];
        if (!translationKey) {
          return String(entry || '');
        }
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry || '') : translated;
      })
      .filter((entry) => entry !== '')
      .join(', ');
  }

  private formatEmergencyContactFieldValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toLowerCase();
        if (normalized === 'phone') {
          return this.translate.instant('PROPERTY_INSTRUCTIONS.PHONE_LABEL');
        }
        if (normalized === 'email') {
          return this.translate.instant('PROPERTY_INSTRUCTIONS.EMAIL_LABEL');
        }
        return String(entry || '');
      })
      .filter((entry) => entry !== '')
      .join(', ');
  }

  private formatCategoryValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values.map((entry) => this.categoryLabel(String(entry))).join(', ');
  }

  private formatRoleValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .filter((entry) => entry !== null && typeof entry !== 'undefined' && String(entry).trim() !== '')
      .map((entry) => this.translateRole(String(entry)))
      .join(', ');
  }

  private translateRole(value: string): string {
    const normalized = value.trim();
    if (!normalized) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    const upperKey = normalized.toUpperCase().replace(/[\s-]+/g, '_');
    const lowerKey = normalized.toLowerCase();
    const translationKeyMap: Record<string, string> = {
      CARETAKER: 'ROLES.CARETAKER',
      PROPERTY_MANAGER: 'ROLES.PROPERTY_MANAGER',
      TECHNICAL_ASSETS_MANAGER: 'ROLES.TECHNICAL_ASSETS_MANAGER',
      CONDOMINIUM_MANAGER: 'ROLES.CONDOMINIUM_MANAGER',
      ACCOUNTANT: 'ROLES.ACCOUNTANT',
      COMPANY_ADMIN: 'ROLES.COMPANY_ADMIN',
      EDITOR: 'ROLES.EDITOR',
      VIEWER: 'ROLES.VIEWER',
      company_admin: 'ROLES.COMPANY_ADMIN',
      editor: 'ROLES.EDITOR',
      viewer: 'ROLES.VIEWER',
    };

    const translationKey = translationKeyMap[upperKey] || translationKeyMap[lowerKey];
    if (!translationKey) {
      return normalized;
    }

    const translated = this.translate.instant(translationKey);
    return translated === translationKey ? normalized : translated;
  }

  private formatAddressDetailValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    const map: Record<string, string> = {
      street: 'AUDIT_LOGS.ADDRESS_DETAILS.street',
      zipcode: 'AUDIT_LOGS.ADDRESS_DETAILS.zipcode',
      city: 'AUDIT_LOGS.ADDRESS_DETAILS.city',
    };
    return values
      .map((entry) => {
        const normalized = String(entry);
        const key = map[normalized];
        if (!key) {
          return normalized;
        }
        const translated = this.translate.instant(key);
        return translated === key ? normalized : translated;
      })
      .join(', ');
  }

  private formatTemplateHeaderValue(value: any, field: string): string {
    const values = Array.isArray(value) ? value : [value];
    const managerHeaderKeyMap: Record<string, string> = {
      PROPERTY_MANAGER_NAME: 'PROPERTY_LIST.PROPERTY_MANAGER',
      PROPERTY_MANAGER_EMAIL: 'PROPERTY_LIST.PROPERTY_MANAGER_EMAIL',
      PROPERTY_MANAGER_PHONE: 'PROPERTY_LIST.PROPERTY_MANAGER_PHONE',
      PROPERTY_MANAGER_MOBILE: 'PROPERTY_LIST.PROPERTY_MANAGER_MOBILE',
      TECHNICAL_ASSETS_MANAGER_NAME: 'PROPERTY_LIST.TECHNICAL_ASSETS_MANAGER',
      TECHNICAL_ASSETS_MANAGER_EMAIL: 'PROPERTY_LIST.TECHNICAL_ASSETS_MANAGER_EMAIL',
      TECHNICAL_ASSETS_MANAGER_PHONE: 'PROPERTY_LIST.TECHNICAL_ASSETS_MANAGER_PHONE',
      TECHNICAL_ASSETS_MANAGER_MOBILE: 'PROPERTY_LIST.TECHNICAL_ASSETS_MANAGER_MOBILE',
      CONDOMINIUM_MANAGER_NAME: 'PROPERTY_LIST.CONDOMINIUM_MANAGER',
      CONDOMINIUM_MANAGER_EMAIL: 'PROPERTY_LIST.CONDOMINIUM_MANAGER_EMAIL',
      CONDOMINIUM_MANAGER_PHONE: 'PROPERTY_LIST.CONDOMINIUM_MANAGER_PHONE',
      CONDOMINIUM_MANAGER_MOBILE: 'PROPERTY_LIST.CONDOMINIUM_MANAGER_MOBILE',
      ACCOUNTANT_NAME: 'PROPERTY_LIST.ACCOUNTANT',
      ACCOUNTANT_EMAIL: 'PROPERTY_LIST.ACCOUNTANT_EMAIL',
      ACCOUNTANT_PHONE: 'PROPERTY_LIST.ACCOUNTANT_PHONE',
      ACCOUNTANT_MOBILE: 'PROPERTY_LIST.ACCOUNTANT_MOBILE',
      CARETAKER_NAME: 'PROPERTY_LIST.CARETAKER_NAME',
      CARETAKER_EMAIL: 'PROPERTY_LIST.CARETAKER_EMAIL',
      CARETAKER_PHONE: 'PROPERTY_LIST.CARETAKER_PHONE',
      CARETAKER_MOBILE: 'PROPERTY_LIST.CARETAKER_MOBILE',
      CARETAKER_ADDRESS: 'PROPERTY_LIST.ADDRESS',
    };
    const resolved = values
      .map((entry) => {
        const normalized = String(entry);
        if (field === 'technicalServiceHeaders') {
          return this.translateServiceType(normalized);
        }
        if (field === 'addressDetails') {
          return this.formatAddressDetailValue(normalized);
        }
        const managerHeaderKey = managerHeaderKeyMap[normalized];
        if (managerHeaderKey) {
          const translated = this.translate.instant(managerHeaderKey);
          if (normalized === 'PROPERTY_MANAGER_NAME' || normalized === 'TECHNICAL_ASSETS_MANAGER_NAME' || normalized === 'CONDOMINIUM_MANAGER_NAME' || normalized === 'ACCOUNTANT_NAME') {
            const nameLabel = this.translate.instant('PROPERTY_LIST.NAME');
            return `${translated} ${nameLabel}`.trim();
          }
          return translated === managerHeaderKey ? normalized : translated;
        }
        if (/^[A-Z0-9_]+$/.test(normalized)) {
          const translated = this.translate.instant(normalized);
          return translated === normalized ? normalized : translated;
        }
        const fallbackKey = this.fieldLabelKeys[normalized];
        if (fallbackKey) {
          return this.translate.instant(fallbackKey);
        }
        return normalized;
      })
      .filter(Boolean);

    return resolved.length ? resolved.join(', ') : this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
  }

  private formatPermissionValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .filter((entry) => entry !== null && typeof entry !== 'undefined' && String(entry).trim() !== '')
      .map((entry) => {
        const permission = String(entry);
        const key = this.permissionLabelKeys[permission];
        if (!key) {
          return permission;
        }
        const translated = this.translate.instant(key);
        return translated === key ? permission : translated;
      })
      .join(', ');
  }

  private formatPersonReferenceValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    const resolved = values
      .filter((entry) => entry !== null && typeof entry !== 'undefined' && String(entry).trim() !== '')
      .map((entry) => this.personLookup.get(String(entry)) || this.userLookup.get(String(entry)) || String(entry));
    return resolved.length ? resolved.join(', ') : this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
  }

  private formatServiceReferenceValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .filter((entry) => entry !== null && typeof entry !== 'undefined' && String(entry).trim() !== '')
      .map((entry) => this.serviceLookup.get(String(entry)) || String(entry))
      .join(', ');
  }

  private formatTechnicalServiceAssignments(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => this.formatInlineValue(entry, 'technicalServiceAssignments'))
      .join(', ');
  }

  private formatEmergencyStepsValue(value: any): string {
    const steps = Array.isArray(value) ? value : [];
    if (!steps.length) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }

    return steps
      .map((step: any, index: number) => {
        const title = step?.title || this.translate.instant('PROPERTY_INSTRUCTIONS.STEP_UNTITLED');
        const description = step?.description ? `: ${step.description}` : '';
        const urgency = step?.urgency ? this.formatEmergencyUrgencyValue(step.urgency) : '';
        const actionType = step?.actionType ? this.formatEmergencyActionTypeValue(step.actionType) : '';
        const details = [urgency, actionType].filter(Boolean).join(' · ');
        const suffix = details ? ` (${details})` : '';
        return `${index + 1}. ${title}${description}${suffix}`;
      })
      .join(' | ');
  }

  private formatEmergencyActionTypeValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toUpperCase();
        const translationKey = `PROPERTY_INSTRUCTIONS.ACTION_TYPE.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatInviteStatusValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toLowerCase();
        const translationKey = `AUDIT_LOGS.VALUES.INVITE_STATUS.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private isSignOrderAuditLog(log?: AuditLogEntry | null): boolean {
    return log?.resourceType === 'SignOrder';
  }

  private formatSignOrderStatusValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toUpperCase();
        const translationKey = `SIGN_ORDER.STATUS.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  private formatBrandedDomainStatusValue(value: any): string {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((entry) => {
        const normalized = String(entry || '').trim().toLowerCase();
        const translationKey = `AUDIT_LOGS.VALUES.BRANDED_DOMAIN_STATUS.${normalized}`;
        const translated = this.translate.instant(translationKey);
        return translated === translationKey ? String(entry) : translated;
      })
      .join(', ');
  }

  getPropertyServiceChangeGroups(log: AuditLogEntry | null): PropertyServiceChangeGroup[] {
    if (!log || log.resourceType !== 'property') {
      return [];
    }

    const beforeAssignments = this.normalizePropertyServiceAssignments(log.changes?.before?.technicalServiceAssignments, log.changes?.before?.technicalServiceIds);
    const afterAssignments = this.normalizePropertyServiceAssignments(log.changes?.after?.technicalServiceAssignments, log.changes?.after?.technicalServiceIds);
    const allTypes = Array.from(new Set([...beforeAssignments.keys(), ...afterAssignments.keys()])).sort((left, right) => left.localeCompare(right));

    return allTypes
      .map((serviceType) => {
        const beforeSet = beforeAssignments.get(serviceType) || new Set<string>();
        const afterSet = afterAssignments.get(serviceType) || new Set<string>();
        const addedServices = Array.from(afterSet).filter((service) => !beforeSet.has(service)).sort((left, right) => left.localeCompare(right));
        const removedServices = Array.from(beforeSet).filter((service) => !afterSet.has(service)).sort((left, right) => left.localeCompare(right));
        return {
          serviceType,
          addedServices,
          removedServices,
        };
      })
      .filter((group) => group.addedServices.length || group.removedServices.length);
  }

  private normalizePropertyServiceAssignments(assignments: any, serviceIds: any): Map<string, Set<string>> {
    const grouped = new Map<string, Set<string>>();

    const pushValue = (serviceType: string, serviceLabel: string) => {
      const normalizedType = serviceType || this.translate.instant('AUDIT_LOGS.SERVICES.UNSPECIFIED_TYPE');
      const current = grouped.get(normalizedType) || new Set<string>();
      current.add(serviceLabel);
      grouped.set(normalizedType, current);
    };

    (Array.isArray(assignments) ? assignments : []).forEach((entry) => {
      const rawType = entry?.serviceType ? this.translateServiceType(String(entry.serviceType)) : this.translate.instant('AUDIT_LOGS.SERVICES.UNSPECIFIED_TYPE');
      const rawServiceId = entry?.serviceId;
      const serviceLabel = this.serviceLookup.get(String(rawServiceId || '')) || String(rawServiceId || this.translate.instant('AUDIT_LOGS.VALUES.EMPTY'));
      pushValue(rawType, serviceLabel);
    });

    (Array.isArray(serviceIds) ? serviceIds : []).forEach((serviceId) => {
      const serviceLabel = this.serviceLookup.get(String(serviceId)) || String(serviceId);
      const alreadyAssigned = Array.from(grouped.values()).some((services) => services.has(serviceLabel));
      if (!alreadyAssigned) {
        pushValue(this.translate.instant('AUDIT_LOGS.SERVICES.UNSPECIFIED_TYPE'), serviceLabel);
      }
    });

    return grouped;
  }

  getBulkDetailItems(log: AuditLogEntry | null): BulkDetailItem[] {
    if (!log || (!log.bulk && !log.meta)) {
      return [];
    }

    const items: BulkDetailItem[] = [];
    const counts = log.bulk?.counts || {};

    if (log.meta?.sourcePropertyId) {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.SOURCE_PROPERTY'),
        value: String(log.meta.sourcePropertyLabel || this.resolvePropertyLabel(log.meta.sourcePropertyId)),
      });
    }
    if (log.meta?.sectionTitle) {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.SECTION'),
        value: String(log.meta.sectionTitle),
      });
    }
    if (log.meta?.sourceSectionTitle && log.meta?.sourceSectionTitle !== log.meta?.sectionTitle) {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.SECTION'),
        value: String(log.meta.sourceSectionTitle),
      });
    }
    if (log.meta?.sourceInstructionTitle) {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.INSTRUCTION'),
        value: String(log.meta.sourceInstructionTitle),
      });
    }
    if (typeof counts.appliedPropertyCount === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.APPLIED_TO'),
        value: this.translate.instant('AUDIT_LOGS.BULK.PROPERTY_COUNT', { count: counts.appliedPropertyCount }),
      });
    }
    if (typeof counts.createdSections === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.CREATED_SECTIONS'),
        value: String(counts.createdSections),
      });
    }
    if (typeof counts.mergedSections === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.MERGED_SECTIONS'),
        value: String(counts.mergedSections),
      });
    }
    if (typeof counts.addedFiles === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.ADDED_FILES'),
        value: String(counts.addedFiles),
      });
    }
    if (typeof counts.duplicateFilesSkipped === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.SKIPPED_FILES'),
        value: String(counts.duplicateFilesSkipped),
      });
    }
    if (typeof counts.createdInstructions === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.CREATED_INSTRUCTIONS'),
        value: String(counts.createdInstructions),
      });
    }
    if (typeof counts.updated === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.UPDATED'),
        value: String(counts.updated),
      });
    }
    if (typeof counts.deleted === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.DELETED'),
        value: String(counts.deleted),
      });
    }
    if (typeof counts.notFound === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.NOT_FOUND'),
        value: String(counts.notFound),
      });
    }
    if (typeof counts.skipped === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.SKIPPED'),
        value: String(counts.skipped),
      });
    }
    if (typeof counts.errors === 'number') {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.ERRORS'),
        value: String(counts.errors),
      });
    }
    if (Array.isArray(log.bulk?.targetIds) && log.bulk.targetIds.length) {
      items.push({
        label: this.translate.instant('AUDIT_LOGS.BULK.TARGET_PROPERTIES'),
        value: log.bulk.targetIds.map((id: string) => this.resolvePropertyLabel(id)).join(', '),
      });
    }

    return items;
  }

  getBulkTitle(log: AuditLogEntry | null): string {
    if (log?.action === 'document.section.bulk_applied' || log?.action === 'document_section.bulk_applied' || log?.action === 'document_section.bulk_edited' || log?.action === 'document_section.bulk_deleted') {
      return this.translate.instant('AUDIT_LOGS.DETAIL.DOCUMENT_DISTRIBUTION');
    }
    if (log?.action === 'emergency_instruction.bulk_applied' || log?.action === 'emergency_instruction.bulk_edited' || log?.action === 'emergency_instruction.bulk_deleted') {
      return this.translate.instant('AUDIT_LOGS.DETAIL.INSTRUCTION_DISTRIBUTION');
    }
    return this.translate.instant('AUDIT_LOGS.DETAIL.DISTRIBUTION');
  }

  private resolvePropertyLabel(propertyId: string): string {
    return this.propertyLookup.get(String(propertyId)) || String(propertyId);
  }

  private logContainsField(log: AuditLogEntry, fieldName: string): boolean {
    const explicitEntries = Array.isArray(log.changes?.diff?.entries) ? (log.changes?.diff?.entries as AuditDiffEntry[]) : [];
    if (explicitEntries.some((entry) => entry.field === fieldName)) {
      return true;
    }

    const before = log.changes?.before;
    const after = log.changes?.after;
    return Object.prototype.hasOwnProperty.call(before || {}, fieldName) || Object.prototype.hasOwnProperty.call(after || {}, fieldName);
  }

  private formatAvailabilityValue(value: any): string {
    const slots = Array.isArray(value)
      ? value
      : (value && typeof value === 'object' && value.start && value.end ? [value] : []);
    if (!slots.length) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }
    return slots
      .map((slot) => {
        const start = this.formatTimeValue(slot?.start);
        const end = this.formatTimeValue(slot?.end);
        return start && end ? `${start}-${end}` : '';
      })
      .filter(Boolean)
      .join(', ');
  }

  private formatAvailabilitySummaryValue(value: any): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }
    const entries = Object.entries(value);
    if (!entries.length) {
      return this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
    }
    return entries
      .map(([day, slots]) => {
        const dayLabel = this.dayLabel(day);
        const formatted = this.formatAvailabilityValue(slots);
        return formatted ? `${dayLabel}: ${formatted}` : '';
      })
      .filter(Boolean)
      .join('; ') || this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
  }

  private formatTimeValue(value: any): string {
    if (!value) {
      return '';
    }
    const stringValue = String(value);
    if (/^\d{2}:\d{2}$/.test(stringValue)) {
      return stringValue;
    }
    const parsed = new Date(stringValue);
    if (Number.isNaN(parsed.getTime())) {
      return stringValue;
    }
    const locale = (this.translate.currentLang || 'en').toLowerCase() === 'de' ? 'de-DE' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  }

  getDocumentChangeItems(log: AuditLogEntry | null): DocumentChangeItem[] {
    if (!log || (!log.action.startsWith('document.') && !log.action.startsWith('document_section.'))) {
      return [];
    }

    if (log.action === 'document.file.deleted') {
      const sectionTitle = log.changes?.before?.sectionTitle || this.translate.instant('AUDIT_LOGS.DOCUMENT.UNTITLED_SECTION');
      const fileName = log.changes?.before?.file?.fileName;
      return fileName ? [{
        sectionTitle,
        sectionAdded: false,
        sectionRemoved: false,
        addedFiles: [],
        removedFiles: [fileName],
      }] : [];
    }

    if (log.action === 'document.section.deleted') {
      const beforeSection = log.changes?.before;
      const sectionTitle = beforeSection?.title || this.translate.instant('AUDIT_LOGS.DOCUMENT.UNTITLED_SECTION');
      const removedFiles = Array.isArray(beforeSection?.files)
        ? beforeSection.files.map((file: any) => file?.fileName).filter(Boolean)
        : [];
      return [{
        sectionTitle,
        sectionAdded: false,
        sectionRemoved: true,
        addedFiles: [],
        removedFiles,
      }];
    }

    const beforeSections = this.indexDocumentSections(log.changes?.before?.sections);
    const afterSections = this.indexDocumentSections(log.changes?.after?.sections);
    const sectionTitles = Array.from(new Set([...beforeSections.keys(), ...afterSections.keys()]))
      .sort((left, right) => left.localeCompare(right));

    return sectionTitles
      .map((sectionTitle) => {
        const beforeFiles = beforeSections.get(sectionTitle) || [];
        const afterFiles = afterSections.get(sectionTitle) || [];
        const beforeSet = new Set(beforeFiles);
        const afterSet = new Set(afterFiles);
        const addedFiles = afterFiles.filter((fileName) => !beforeSet.has(fileName));
        const removedFiles = beforeFiles.filter((fileName) => !afterSet.has(fileName));
        return {
          sectionTitle,
          sectionAdded: !beforeFiles.length && afterFiles.length > 0,
          sectionRemoved: beforeFiles.length > 0 && !afterFiles.length,
          addedFiles,
          removedFiles,
        };
      })
      .filter((item) => item.sectionAdded || item.sectionRemoved || item.addedFiles.length || item.removedFiles.length);
  }

  private indexDocumentSections(sections: any): Map<string, string[]> {
    const map = new Map<string, string[]>();
    (Array.isArray(sections) ? sections : []).forEach((section: DocumentSectionSummary) => {
      const title = section?.title || this.translate.instant('AUDIT_LOGS.DOCUMENT.UNTITLED_SECTION');
      const files = Array.isArray(section?.files)
        ? section.files
            .map((file) => file?.fileName)
            .filter((fileName): fileName is string => typeof fileName === 'string' && fileName.trim().length > 0)
            .sort((left, right) => left.localeCompare(right))
        : [];
      map.set(title, files);
    });
    return map;
  }

  getSummaryParts(log: AuditLogEntry): string[] {
    const parts: string[] = [];
    if (this.getLogKind(log) === 'activity') {
      parts.push(this.getActivitySummary(log));
      return parts;
    }

    const changedFields = this.getChangedFields(log);
    if (changedFields.length) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.CHANGED_FIELDS', { count: changedFields.length }));
    }

    if (log.action === 'property.imported') {
      const summary = log.changes?.summary || {};
      if (typeof summary.rowsReceived === 'number') {
        parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.ROWS_RECEIVED', { count: summary.rowsReceived }));
      }
      if (typeof summary.createdCount === 'number') {
        parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.CREATED_PROPERTIES', { count: summary.createdCount }));
      }
      if (typeof summary.skippedCount === 'number' && summary.skippedCount > 0) {
        parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.SKIPPED_PROPERTIES', { count: summary.skippedCount }));
      }
      if (typeof summary.clearedCaretakerContactPersonSelections === 'number'
        && summary.clearedCaretakerContactPersonSelections > 0) {
        parts.push(this.translate.instant(
          'AUDIT_LOGS.SUMMARY.CLEARED_CARETAKER_CONTACT_PERSON_SELECTIONS',
          { count: summary.clearedCaretakerContactPersonSelections }
        ));
      }
      if (summary.importFileName) {
        parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.IMPORT_FILE', { name: summary.importFileName }));
      }
    }

    const resetEmergencyInstructionActions = log.changes?.summary?.resetEmergencyInstructionActions;
    if (typeof resetEmergencyInstructionActions === 'number' && resetEmergencyInstructionActions > 0) {
      parts.push(this.translate.instant(
        'AUDIT_LOGS.SUMMARY.RESET_EMERGENCY_INSTRUCTION_ACTIONS',
        { count: resetEmergencyInstructionActions }
      ));
    }

    const bulkCounts = log.bulk?.counts || {};
    if (typeof bulkCounts.appliedPropertyCount === 'number') {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.TARGET_PROPERTIES', { count: bulkCounts.appliedPropertyCount }));
    }
    if (typeof bulkCounts.createdSections === 'number' && bulkCounts.createdSections > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.CREATED_SECTIONS', { count: bulkCounts.createdSections }));
    }
    if (typeof bulkCounts.mergedSections === 'number' && bulkCounts.mergedSections > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.MERGED_SECTIONS', { count: bulkCounts.mergedSections }));
    }
    if (typeof bulkCounts.duplicateFilesSkipped === 'number' && bulkCounts.duplicateFilesSkipped > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.SKIPPED_FILES', { count: bulkCounts.duplicateFilesSkipped }));
    }
    if (typeof bulkCounts.createdInstructions === 'number' && bulkCounts.createdInstructions > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.CREATED_INSTRUCTIONS', { count: bulkCounts.createdInstructions }));
    }
    if (typeof bulkCounts.updated === 'number' && bulkCounts.updated > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.UPDATED_ITEMS', { count: bulkCounts.updated }));
    }
    if (typeof bulkCounts.deleted === 'number' && bulkCounts.deleted > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.DELETED_ITEMS', { count: bulkCounts.deleted }));
    }
    if (typeof bulkCounts.notFound === 'number' && bulkCounts.notFound > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.NOT_FOUND_ITEMS', { count: bulkCounts.notFound }));
    }
    if (typeof bulkCounts.skipped === 'number' && bulkCounts.skipped > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.SKIPPED_ITEMS', { count: bulkCounts.skipped }));
    }
    if (typeof bulkCounts.errors === 'number' && bulkCounts.errors > 0) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.ERROR_ITEMS', { count: bulkCounts.errors }));
    }
    const sectionCount = log.changes?.summary?.sectionCount;
    if (typeof sectionCount === 'number' && !log.action.startsWith('document.') && !log.action.startsWith('document_section.')) {
      parts.push(this.translate.instant('AUDIT_LOGS.SUMMARY.SECTIONS', { count: sectionCount }));
    }
    return parts;
  }

  private buildFilters() {
    return {
      page: this.pageIndex,
      pageSize: this.pageSize,
      search: this.search,
      actor: this.actor,
      logKind: this.selectedLogKind || undefined,
      action: this.selectedAction || undefined,
      resourceType: this.selectedResourceType || undefined,
      dateFrom: this.dateRange?.[0] ? this.toDateParam(this.dateRange[0]) : undefined,
      dateTo: this.dateRange?.[1] ? this.toDateParam(this.dateRange[1]) : undefined,
    };
  }

  private toDateParam(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private resolveAuditFieldTranslationKey(fieldPath: string): string | null {
    const exact = this.fieldLabelKeys[fieldPath];
    if (exact) {
      return exact;
    }

    const lastSegment = fieldPath.split('.').pop() || fieldPath;
    const lastSegmentExact = this.fieldLabelKeys[lastSegment];
    if (lastSegmentExact) {
      return lastSegmentExact;
    }

    const candidates = [fieldPath, lastSegment]
      .map((entry) => this.normalizeAuditFieldKey(entry))
      .filter(Boolean);

    for (const candidate of candidates) {
      const fieldKey = this.auditFieldAliases[candidate] || candidate;
      const translationKey = this.fieldLabelKeys[fieldKey];
      if (translationKey) {
        return translationKey;
      }
    }

    return null;
  }

  private humanizeAuditAction(action: string): string {
    const normalized = String(action || '').trim();
    if (!normalized) {
      return this.translate.instant('AUDIT_LOGS.UNKNOWN_ACTION');
    }
    return normalized
      .split(/[._]+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private humanizeAuditFieldLabel(fieldPath: string): string {
    const lastSegment = String(fieldPath || '').split('.').pop() || String(fieldPath || '');
    return this.normalizeAuditFieldKey(lastSegment)
      .split('_')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private normalizeAuditFieldKey(field: string): string {
    return String(field || '')
      .trim()
      .replace(/\./g, '_')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  }

  private shortenResourceId(value: any): string {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return '';
    }
    if (normalized.length <= 12) {
      return normalized;
    }
    return `${normalized.slice(0, 8)}…${normalized.slice(-4)}`;
  }

  private tryParseJsonValue(value: string): any {
    const trimmed = value.trim();
    if (!trimmed || !/^[\[{]/.test(trimmed)) {
      return undefined;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }

  private looksLikeFileSummary(value: any): boolean {
    return Boolean(value && typeof value === 'object' && (value.fileName || value.fileType || value.uploadedAt));
  }

  private isFileSummaryArray(value: any[]): boolean {
    return Array.isArray(value) && value.length > 0 && value.every((entry) => this.looksLikeFileSummary(entry));
  }

  private formatDocumentFileSummary(value: any): string {
    const fileName = typeof value?.fileName === 'string' ? value.fileName.trim() : '';
    if (fileName) {
      return fileName;
    }
    const fileType = typeof value?.fileType === 'string' ? value.fileType.trim() : '';
    return fileType || this.translate.instant('AUDIT_LOGS.VALUES.DETAILS_CHANGED');
  }

  private formatFileList(value: any[]): string {
    const names = value
      .map((entry) => this.formatDocumentFileSummary(entry))
      .filter((entry) => Boolean(entry) && entry !== this.translate.instant('AUDIT_LOGS.VALUES.DETAILS_CHANGED'));
    return names.length ? names.join(', ') : this.translate.instant('AUDIT_LOGS.VALUES.EMPTY');
  }
}
